import PropTypes from 'prop-types';
import React from 'react';

import { isLocalURL } from '$utilities/browser';
import { formatLinkBlockToUrl } from '$utilities/format';
import IconArrowRight from '$partials/icons/icon-arrow-right';
import IconDownload from '$partials/icons/icon-download';
import Button from '$partials/ui/button.jsx';
import RichTextWithGlossary from '$partials/glossary/rich-text-with-glossary.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ModuleLinksListBlock = ({
    headingRichText,
    items,
    columns,
    isAccordionOrSection,
    isFirstInAccordion,
    isDetailPage
}) => {

    return (
        <section className='relative'>
            <div className='layout-container'>
                <div className='layout-grid-negative-gutter flex'>
                    <div className='md:w-1/12' />
                    <div className='w-12/12 md:w-11/12 layout-grid-col-padding'>
                        <div className={((isDetailPage || isAccordionOrSection) && !isFirstInAccordion) ? 'border-t border-t-charcoal' : ''} />
                    </div>
                </div>
            </div>

            <div className={`layout-container ${isAccordionOrSection ? 'mb-60' : 'mb-90'}`}>
                <div className='layout-grid-negative-gutter sm:flex'>
                    <div className='md:w-1/12' />
                    <RichTextWithGlossary
                        className='rich-text py-30 sm:pr-40 type-style-3 layout-grid-col-padding sm:w-4/12'
                        html={headingRichText} />

                    {columns === 'one' && (
                        <ul className='sm:w-8/12 md:w-7/12 layout-grid-col-padding'>
                            {items?.map((item, idx) => {
                                const href = formatLinkBlockToUrl(item?.link);
                                const target = isLocalURL(href) ? '_self' : '_blank';
                                const rel = isLocalURL(href) ? null : 'noreferrer noopener';
                                return (
                                    <React.Fragment key={idx}>
                                        <li className={`${idx === 0 ? 'sm:border-t-0' : ''} border-t border-t-charcoal py-30 flex items-center justify-between`}>
                                            <RichTextWithGlossary
                                                className='type-style-3 rich-text w-6/12'
                                                html={item.label} />
                                            {item.link ? (
                                                <Button
                                                    as='a'
                                                    className='round-button'
                                                    href={href}
                                                    rel={rel}
                                                    renderIcon={<IconArrowRight />}
                                                    target={target} />
                                            ) : (
                                                <Button
                                                    as='a'
                                                    className='round-button'
                                                    href={item.file?.url}
                                                    rel={rel}
                                                    renderIcon={<IconDownload />}
                                                    target='_blank'>
                                                    <span className='sr-only'>
                                                            Download
                                                    </span>
                                                </Button>
                                            )}
                                        </li>
                                    </React.Fragment>
                                );
                            })}
                        </ul>
                    )}

                    {columns === 'two' && (
                        <ul className='flex flex-wrap sm:w-8/12 md:w-7/12'>
                            {items?.map((item, idx) => {
                                const href = formatLinkBlockToUrl(item?.link);
                                const target = isLocalURL(href) ? '_self' : '_blank';
                                const rel = isLocalURL(href) ? null : 'noreferrer noopener';
                                return (
                                    <li
                                        className='layout-grid-col-padding w-6/12'
                                        key={idx}>
                                        <div
                                            className={`${idx === 0 || idx === 1 ? 'sm:border-t-0' : ''} border-t border-t-charcoal py-30 flex items-center justify-between`}>
                                            <RichTextWithGlossary
                                                className='type-style-4 w-6/12 sm:w-8/12 rich-text'
                                                html={item.label} />
                                            {item.link ? (
                                                <Button
                                                    as='a'
                                                    className='round-button'
                                                    href={href}
                                                    rel={rel}
                                                    renderIcon={<IconArrowRight />}
                                                    target={target} />
                                            ) : (
                                                <Button
                                                    as='a'
                                                    className='round-button'
                                                    href={item.file?.url}
                                                    rel={rel}
                                                    renderIcon={<IconDownload />}
                                                    target='_blank'>
                                                    <span className='sr-only'>
                                                            Download
                                                    </span>
                                                </Button>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    headingRichText: PropTypes.string,
    index: PropTypes.number,
    isAccordionOrSection: PropTypes.bool,
    isFirstInAccordion: PropTypes.bool,
    isDetailPage: PropTypes.bool,
    items: PropTypes.array,
    columns: PropTypes.string
};


ModuleLinksListBlock.propTypes = propTypes;

export default ModuleLinksListBlock;
